import React, { createContext, useContext, useState, ReactNode } from 'react';
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react';

type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface NotificationOptions {
    title: string;
    message: string;
    type?: NotificationType;
    duration?: number;
}

interface NotificationContextType {
    showNotification: (options: NotificationOptions) => void;
    hideNotification: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [config, setConfig] = useState<NotificationOptions>({ title: '', message: '', type: 'success' });

    const showNotification = (options: NotificationOptions) => {
        setConfig({ ...options, type: options.type || 'success' });
        setIsOpen(true);

        // Auto-hide only if not an error or if duration is specified
        if (options.type !== 'error' || options.duration) {
            const time = options.duration || 3000;
            setTimeout(() => setIsOpen(false), time);
        }
    };

    const hideNotification = () => setIsOpen(false);

    return (
        <NotificationContext.Provider value={{ showNotification, hideNotification }}>
            {children}
            {isOpen && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
                    {/* Backdrop con Glassmorphism */}
                    <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-md animate-in fade-in cursor-pointer" onClick={hideNotification}></div>

                    {/* Modal Card */}
                    <div className={`
             relative bg-white rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.2)] p-8 flex flex-col sm:flex-row items-center gap-6 max-w-sm w-full border-t-[12px]
             animate-in zoom-in-95 slide-in-from-bottom-8 duration-500
             ${config.type === 'success' ? 'border-emerald-500' :
                            config.type === 'error' ? 'border-rose-500' :
                                config.type === 'warning' ? 'border-amber-500' : 'border-sky-500'}
           `}>

                        {/* Icon Section */}
                        <div className={`
               p-5 rounded-3xl shrink-0 shadow-lg
               ${config.type === 'success' ? 'bg-emerald-100 text-emerald-600 shadow-emerald-100' :
                                config.type === 'error' ? 'bg-rose-100 text-rose-600 shadow-rose-100' :
                                    config.type === 'warning' ? 'bg-amber-100 text-amber-600 shadow-amber-100' : 'bg-sky-100 text-sky-600 shadow-sky-100'}
             `}>
                            {config.type === 'success' && <CheckCircle2 size={48} strokeWidth={2.5} />}
                            {(config.type === 'error' || config.type === 'warning') && <AlertTriangle size={48} strokeWidth={2.5} />}
                            {config.type === 'info' && <Info size={48} strokeWidth={2.5} />}
                        </div>

                        {/* Text Content */}
                        <div className="text-center sm:text-left flex-1">
                            <h4 className="text-2xl font-black text-slate-800 uppercase italic leading-none tracking-tighter mb-2">
                                {config.title}
                            </h4>
                            <p className="text-slate-500 font-bold text-sm leading-relaxed">
                                {config.message}
                            </p>
                        </div>

                        {/* Close button (Mobile top right or just useful everywhere) */}
                        <button
                            onClick={hideNotification}
                            className="absolute top-4 right-4 text-slate-300 hover:text-slate-500 transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>
            )}
        </NotificationContext.Provider>
    );
};

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (context === undefined) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};
